import { jsx } from 'react/jsx-runtime';
import { ChatbotMessageRole, PermissionRole, ChatbotErrorCodes } from '@fabric/woody-client';
import { captureException } from '@fabric-app/compat/capabilities/sentry';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, createContext, useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { v4 } from 'uuid';
import { shallow as shallow$1 } from '../../../node_modules/zustand/esm/shallow.js';
import { AssistantEphemeralContextSubType, ChatbotConversationUserActionSubType, ChatbotConversationUserActionType } from '../types.js';
import { useAssistantStore, useAssistantContext } from '../stores/assistantStore.js';
import { useContextFinder } from './useContextFinder.js';
import { EVERYTHING_CONTEXT } from '../constants.js';
import { getDatabase } from '@fabric-app/compat';
import { populateActionPrompt } from '../utils/populateActionPrompt.js';
import { convertAssistantContextToAPIScope } from '../utils/convertAssistantContextToAPIScope.js';
import { AnalyticsEvents } from '../../analytics/analytics.types.js';
import '../../onboarding/onboarding.config.js';
import { useMutationCheckListItemState } from '../../onboarding/mutations/useMutationCheckListItemState.js';
import { pick } from 'lodash-es';
import { useWoody } from '../../woody/woody.js';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { useDebouncedCallback } from '@fabric-app/hooks/useDebouncedCallback';
import { useUnmount } from '@fabric-app/hooks/useUnmount';
import { useWindowEvent } from '@fabric-app/hooks/useWindowEvent';
import { useIntervalFn } from '@fabric-app/hooks/useIntervalFn';
import { ensureError } from '@fabric-app/utils/guards';
import { useContextAccessToken } from '../../permission/contexts/AnonShareTokenContext.js';
import { createLogger } from '@fabric-app/utils/logging';
import { useAnalytics } from '../../analytics/hooks/useAnalytics.js';
import { getErrorDetail } from '../../errors/errors.utils.js';
import { useQueryResourceDetail } from '../../resources/queries/useQueryResourceDetail.js';
import equal from 'fast-deep-equal';
import { getInitialContextMessageContent } from '../utils/getInitialContextMessage.js';
import { getResourceCaption } from '../../resources/utils/getResourceCaption.js';
import { dispatchFabricEvent, useFabricEvent } from '../../hooks/useFabricEvent.js';
import { isChatbotAssistantMessage } from '../utils/guardsChatbotMessage.js';
import { parseChatbotMessageNodes, simplifiedFormatChatbotMessageNodes, parseChatbotMessage } from '../utils/parseChatbotMessage.js';
import { PENDING_DRAFT_MARKER, NO_PERMISSION_DRAFT_MARKER } from '../utils/filterDiffTags.js';
import { ErrorType } from '../../errors/errors.types.js';
import { showPlanUpgradePromptWithErrorCode } from '../../pricing/utils/showPlanUpgradePrompt.js';
import { useQuerySubscription, useInvalidateQuerySubscription, useQuerySubscriptionKey } from '../../pricing/queries/useQuerySubscription.js';
import { useFeatureGuard_modelSwitching } from '../../pricing/hooks/useFeatureGuard.js';
import { OnboardingCheckListActionId } from '@fabric-app/types/onboarding';

const assistantStoreName = "chat";
const HISTORY_CONTEXT_WINDOW_SIZE = 10;
const MAX_CHAT_INACTIVITY = 1e3 * 60 * 10;
function findFabricUUIDs(s) {
  const regex = /fabric:\/\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}/g;
  const matches = s.match(regex);
  const uuids = matches?.map((match) => match.replace("fabric://", "").trim()) ?? [];
  return [
    ...new Set(uuids)
  ];
}
const logger = createLogger("chatbot", true);
const ChatsContext = /* @__PURE__ */ createContext({});
const contextKey = "default-conversation";
function MessagesProvider({ children, chatAssistantOpen, expandedResourceId }) {
  const { client } = useWoody();
  const { track } = useAnalytics();
  const [diffsActiveMessageId, setDiffsActiveMessageId] = useState(null);
  const [error, setError] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const accessToken = useContextAccessToken();
  const [streamingAnswers, setStreamingAnswers] = useState({});
  const hasUnmounted = useRef(false);
  const { mutate: mutateCheckListItemState } = useMutationCheckListItemState();
  const queryClient = useQueryClient();
  const { data: subscription } = useQuerySubscription();
  const assistantUsage = subscription?.usage.chatbot;
  const creditsUsage = subscription?.usage.credits;
  const invalidateSubscriptionQuery = useInvalidateQuerySubscription();
  const subscriptionQueryKey = useQuerySubscriptionKey();
  const bumpChatbotUsage = useCallback((count = 1) => {
    queryClient.setQueryData(subscriptionQueryKey, (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        usage: {
          ...prev.usage,
          chatbot: {
            limit: prev.usage.chatbot.limit,
            used: prev.usage.chatbot.used + count
          }
        }
      };
    });
    invalidateSubscriptionQuery();
  }, [
    queryClient,
    invalidateSubscriptionQuery,
    subscriptionQueryKey
  ]);
  const { lastContextSubtype, setLastContextSubtype, messages, setMessages, setActionInit, actionInit, setHydratedKey, hydratedKey, addMessage, ephemeralContext, setEphemeralContext: _setEphemeralContext, persistentContext, setPersistentContext, aiModel } = useAssistantStore((s) => pick(s, [
    "aiModel",
    "lastContextSubtype",
    "setLastContextSubtype",
    "messages",
    "setMessages",
    "actionInit",
    "setActionInit",
    "hydratedKey",
    "setHydratedKey",
    "addMessage",
    "ephemeralContext",
    "setEphemeralContext",
    "persistentContext",
    "setPersistentContext"
  ]), shallow$1);
  const { context } = useAssistantContext();
  const contexts = useContextFinder(expandedResourceId);
  const { data: expandedResourceData } = useQueryResourceDetail({
    resourceId: expandedResourceId
  });
  const expandedResource = expandedResourceId ? expandedResourceData : null;
  const addMessageSentAsAssistant = useCallback((message) => {
    if (!message?.content) {
      return;
    }
    const newMessage = {
      id: v4(),
      ephemeralContext,
      persistentContext,
      resourceIds: [],
      resources: [],
      ...message,
      content: message.content ?? "",
      rawContent: message.rawContent ?? "",
      role: ChatbotMessageRole.assistant,
      streaming: false
    };
    addMessage(newMessage);
  }, [
    addMessage,
    ephemeralContext,
    persistentContext
  ]);
  const setEphemeralContext = useCallback((nextContext) => {
    _setEphemeralContext((prevContext) => {
      const lastMessage = messages[messages.length - 1];
      if (!lastMessage || lastMessage.ephemeralContext?.subType !== nextContext.subType && !!lastMessage || !equal(prevContext.value, nextContext.value)) {
        if (nextContext.subType === AssistantEphemeralContextSubType.File) {
          if (expandedResource) {
            const captionSummary = getResourceCaption(expandedResource);
            addMessageSentAsAssistant({
              content: getInitialContextMessageContent({
                subType: nextContext.subType,
                resource: expandedResource
              }),
              type: "context-switch-info",
              ephemeralContext: nextContext,
              summary: captionSummary
            });
          }
        } else {
          addMessageSentAsAssistant({
            content: getInitialContextMessageContent({
              subType: nextContext.subType
            }),
            type: "context-switch-info",
            ephemeralContext: nextContext
          });
        }
      }
      return nextContext;
    });
    setLastContextSubtype(nextContext.subType);
  }, [
    setLastContextSubtype,
    messages,
    addMessageSentAsAssistant,
    expandedResource,
    _setEphemeralContext
  ]);
  const switchToCurrentContext = useCallback(() => {
    const fileContext = contexts.find((c) => c.subType === AssistantEphemeralContextSubType.File);
    if (lastContextSubtype !== AssistantEphemeralContextSubType.Everything && !!lastContextSubtype) {
      const context2 = contexts.find((c) => c.subType === lastContextSubtype);
      if (context2) {
        setEphemeralContext(context2);
      } else {
        setEphemeralContext(EVERYTHING_CONTEXT);
      }
    } else if (fileContext) {
      setEphemeralContext(fileContext);
    } else {
      setEphemeralContext(EVERYTHING_CONTEXT);
    }
  }, [
    contexts,
    lastContextSubtype,
    setEphemeralContext
  ]);
  const [lastInteraction, setLastInteraction] = useState(Date.now());
  const resetSelection = useCallback(() => {
    setPersistentContext({
      resourceIds: [],
      textSnippets: []
    });
    setEphemeralContext(EVERYTHING_CONTEXT);
  }, [
    setEphemeralContext,
    setPersistentContext
  ]);
  const currentContextRef = useRef(contextKey);
  currentContextRef.current = contextKey;
  const save = useCallback(async (options) => {
    if (messages.length === 0 && !options?.shouldSaveEmpty) {
      return;
    }
    await getDatabase()?.setItem(assistantStoreName, contextKey, {
      messages,
      lastInteraction,
      contextKey
    });
  }, [
    messages,
    lastInteraction
  ]);
  const saveDebounced = useDebouncedCallback(save, 200);
  useUnmount(saveDebounced);
  useEffect(() => {
    if (!getDatabase() || hydratedKey && hydratedKey !== contextKey || chatAssistantOpen) return;
    saveDebounced();
  }, [
    saveDebounced,
    chatAssistantOpen,
    hydratedKey
  ]);
  const reset = useCallback(() => {
    setMessages([]);
    switchToCurrentContext();
    setLastInteraction(Date.now());
    setStreamingAnswers({});
    setHydratedKey(contextKey);
    setTimeout(() => {
      save({
        shouldSaveEmpty: true
      });
    }, 200);
    if (diffsActiveMessageId) dispatchFabricEvent("fabric-ai-diff-apply", {
      messageId: diffsActiveMessageId,
      action: "reject",
      source: "assistant",
      resourceId: expandedResourceId
    });
  }, [
    setMessages,
    save,
    setHydratedKey,
    switchToCurrentContext,
    diffsActiveMessageId,
    expandedResourceId
  ]);
  const chatResetRef = useRef(reset);
  chatResetRef.current = reset;
  useRunOnce(() => {
    if (!getDatabase() || hydratedKey === contextKey) {
      switchToCurrentContext();
      return;
    }
    const hydrate = async () => {
      const conversation = await getDatabase()?.getItem(assistantStoreName, contextKey);
      if (!conversation) {
        chatResetRef.current();
        return;
      }
      switchToCurrentContext();
      setLastInteraction(conversation.lastInteraction);
      setMessages(conversation.messages);
      setHydratedKey(contextKey);
    };
    hydrate();
  }, {
    condition: chatAssistantOpen
  });
  const { check: checkModelSwitching } = useFeatureGuard_modelSwitching();
  const streamResponse = useCallback(async (pastMessages, action, incompleteMessage) => {
    if (!subscription) return;
    const contextAtStart = currentContextRef.current;
    const canEditResource = expandedResource?.permission?.role === PermissionRole.OWNER || expandedResource?.permission?.role === PermissionRole.EDITOR;
    setDiffsActiveMessageId(null);
    mutateCheckListItemState({
      actionId: OnboardingCheckListActionId.ASK_AI_SOMETHING,
      state: true
    });
    try {
      const [actionedPastMessages] = populateActionPrompt(pastMessages, contexts);
      const temporaryStreamingAnswer = {
        id: incompleteMessage?.id ?? v4(),
        role: ChatbotMessageRole.assistant,
        streaming: true,
        content: incompleteMessage?.content ?? "",
        rawContent: incompleteMessage?.rawContent ?? "",
        resourceIds: incompleteMessage?.resourceIds ?? [],
        resources: incompleteMessage?.resources ?? [],
        ephemeralContext,
        action
      };
      setError(null);
      const history = [
        ...actionedPastMessages.filter((message2) => message2.id !== incompleteMessage?.id).map((message2) => {
          return {
            role: message2.role,
            content: message2.rawContent ?? message2.content
          };
        }),
        ...incompleteMessage ? [
          {
            role: ChatbotMessageRole.assistant,
            content: `${incompleteMessage.rawContent ?? incompleteMessage.content ?? ""}
-- Interrupted here`
          },
          {
            role: ChatbotMessageRole.user,
            content: "Please continue."
          }
        ] : []
      ];
      let iterable;
      const apiScope = convertAssistantContextToAPIScope(context, ephemeralContext);
      try {
        iterable = await client.v2("/v2/chatbot", {
          method: "post",
          body: {
            history,
            continue: !!incompleteMessage,
            model: checkModelSwitching(void 0).isAllowed ? aiModel : void 0,
            ...apiScope
          },
          ...{
            query: {
              accessToken
            }
          }
        });
        if (!iterable) {
          throw new Error("No iterable returned from chatbot");
        }
        bumpChatbotUsage();
      } catch (error2) {
        logger.error("streamResponse - error", error2);
        if (getErrorDetail(error2) === ErrorType.EXCEEDS_CHATBOT_LIMIT) {
          setError(ChatbotErrorCodes.USAGE_LIMIT_EXCEEDED);
          showPlanUpgradePromptWithErrorCode(ErrorType.EXCEEDS_CHATBOT_LIMIT);
          track(AnalyticsEvents.ChatbotQuotaLimitHit, {
            fromMessage: true,
            creditsUsed: subscription?.usage.credits?.used,
            creditsLimit: subscription?.usage.credits?.limit,
            historySize: history.length
          });
          invalidateSubscriptionQuery();
        } else {
          setError(error2.message || ChatbotErrorCodes.INTERNAL_SERVER_ERROR);
          captureException(ensureError(error2), {
            extra: {
              ...apiScope
            }
          });
        }
        setStreamingAnswers((prev) => ({
          ...prev,
          [contextAtStart]: null
        }));
        setActionInit(void 0);
        return;
      }
      for await (const chunk of iterable) {
        setLoadingStates((prev) => ({
          ...prev,
          [contextAtStart]: false
        }));
        temporaryStreamingAnswer.rawContent += chunk;
        const nodes = parseChatbotMessageNodes(temporaryStreamingAnswer.rawContent ?? temporaryStreamingAnswer.content);
        temporaryStreamingAnswer.content = simplifiedFormatChatbotMessageNodes(nodes, canEditResource ? {
          completeDiff: PENDING_DRAFT_MARKER,
          incompleteDiff: PENDING_DRAFT_MARKER
        } : {
          completeDiff: NO_PERMISSION_DRAFT_MARKER,
          incompleteDiff: NO_PERMISSION_DRAFT_MARKER
        });
        setStreamingAnswers((prev) => ({
          ...prev,
          [contextAtStart]: {
            ...temporaryStreamingAnswer
          }
        }));
      }
      const { diffs, message, simplifiedMessage, isIncomplete } = parseChatbotMessage(temporaryStreamingAnswer.rawContent ?? temporaryStreamingAnswer.content, !canEditResource ? {
        completeDiff: NO_PERMISSION_DRAFT_MARKER,
        incompleteDiff: NO_PERMISSION_DRAFT_MARKER
      } : void 0);
      const finalAnswer = {
        ...temporaryStreamingAnswer,
        content: simplifiedMessage,
        rawContent: message,
        streaming: false,
        resourceIds: findFabricUUIDs(message),
        diffs,
        hasIncompleteDiffs: isIncomplete,
        diffsResourceId: expandedResourceId
      };
      setMessages((prevMessages) => [
        ...prevMessages,
        finalAnswer
      ]);
      const diffsResourceId = finalAnswer.diffsResourceId;
      if (diffsResourceId && finalAnswer.diffs.length > 0 && canEditResource) {
        setTimeout(() => {
          dispatchFabricEvent("fabric-ai-diff-propose", {
            diffs: finalAnswer.diffs,
            messageId: finalAnswer.id,
            override: true,
            resourceId: diffsResourceId
          });
        }, 10);
      }
      if (hasUnmounted.current) {
        saveDebounced();
      }
      setActionInit(void 0);
      setStreamingAnswers((prev) => ({
        ...prev,
        [contextAtStart]: null
      }));
    } catch (error2) {
      console.error("error being caught", error2);
      setError(error2.message || ChatbotErrorCodes.INTERNAL_SERVER_ERROR);
      setActionInit(void 0);
      setStreamingAnswers((prev) => ({
        ...prev,
        [contextAtStart]: null
      }));
    }
  }, [
    checkModelSwitching,
    subscription,
    expandedResource?.permission?.role,
    mutateCheckListItemState,
    contexts,
    ephemeralContext,
    context,
    expandedResourceId,
    setMessages,
    setActionInit,
    client,
    aiModel,
    accessToken,
    bumpChatbotUsage,
    track,
    invalidateSubscriptionQuery,
    saveDebounced
  ]);
  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    localStorage.removeItem("assistant-messages");
    localStorage.removeItem("assistant-lastInteraction");
    setDiffsActiveMessageId(null);
  }, [
    setMessages
  ]);
  const sendMessage = useCallback(async (content, action, rawContent) => {
    if (loadingStates[currentContextRef.current]) return;
    setLoadingStates((prev) => ({
      ...prev,
      [currentContextRef.current]: true
    }));
    setLastInteraction(Date.now());
    try {
      const newMessage = {
        id: v4(),
        role: ChatbotMessageRole.user,
        content,
        rawContent: rawContent ?? content,
        ephemeralContext,
        action
      };
      const newMessages = [
        ...messages,
        newMessage
      ];
      setDiffsActiveMessageId(null);
      setMessages(newMessages);
      const contextWindow = [];
      for (let i = newMessages.length - 1; i >= 0; i--) {
        const forMsg = newMessages[i];
        if (forMsg.ephemeralContext?.type === ephemeralContext.type && forMsg.type !== "context-switch-info") {
          contextWindow.unshift(forMsg);
        }
        if (contextWindow.length >= HISTORY_CONTEXT_WINDOW_SIZE) {
          break;
        }
      }
      track(AnalyticsEvents.ChatbotChatMessage, {
        creditsUsage,
        assistantUsage,
        historySize: contextWindow.length,
        contextType: ephemeralContext.type,
        contextName: ephemeralContext.name,
        contextValue: ephemeralContext.value
      });
      await streamResponse(contextWindow, action);
    } catch (error2) {
      console.log("error", error2);
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        [currentContextRef.current]: false
      }));
    }
  }, [
    loadingStates,
    ephemeralContext,
    messages,
    setMessages,
    track,
    streamResponse,
    assistantUsage,
    creditsUsage
  ]);
  const continueMessage = useCallback(async (message) => {
    if (!isChatbotAssistantMessage(message) || !message.hasIncompleteDiffs) return;
    setLastInteraction(Date.now());
    try {
      setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== message.id));
      setStreamingAnswers((prev) => ({
        ...prev,
        [currentContextRef.current]: {
          ...message,
          streaming: true,
          role: ChatbotMessageRole.assistant,
          hasIncompleteDiffs: false,
          diffs: []
        }
      }));
      await streamResponse(messages, void 0, message);
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        [currentContextRef.current]: false
      }));
    }
  }, [
    messages,
    setMessages,
    currentContextRef,
    setLastInteraction,
    streamResponse
  ]);
  const retry = useCallback(async () => {
    if (!error) return;
    setError(null);
    const lastMessage = messages[messages.length - 1];
    await sendMessage(lastMessage.content);
  }, [
    error,
    messages,
    sendMessage
  ]);
  useEffect(() => {
    if (!actionInit || !!loadingStates[contextKey] || !assistantUsage || !!streamingAnswers[contextKey]) return;
    const timeout = setTimeout(() => {
      sendMessage(actionInit.text, {
        // This prevents multiple summarizes from sharing the same info tag UI.
        id: v4(),
        ...actionInit
      });
    }, 50);
    return () => {
      clearTimeout(timeout);
    };
  }, [
    actionInit,
    sendMessage,
    setActionInit,
    loadingStates,
    assistantUsage,
    streamingAnswers
  ]);
  const value = useMemo(() => ({
    messages,
    contexts,
    addMessage: sendMessage,
    clearMessages,
    retry,
    isLoading: !!loadingStates[contextKey],
    isStreamingAnswer: !!streamingAnswers[contextKey],
    streamingAnswer: streamingAnswers[contextKey],
    lastInteraction,
    error,
    ephemeralContext,
    setEphemeralContext,
    persistentContext,
    setPersistentContext,
    resetSelection,
    addMessageSentAsAssistant,
    expandedResource,
    continueMessage,
    diffsActiveMessageId
  }), [
    messages,
    contexts,
    sendMessage,
    clearMessages,
    retry,
    loadingStates,
    streamingAnswers,
    lastInteraction,
    error,
    ephemeralContext,
    setEphemeralContext,
    persistentContext,
    setPersistentContext,
    resetSelection,
    addMessageSentAsAssistant,
    expandedResource,
    continueMessage,
    diffsActiveMessageId
  ]);
  useUnmount(() => {
    setLastInteraction(Date.now());
    hasUnmounted.current = true;
  }, {
    avoidStrictMode: true
  });
  useWindowEvent("beforeunload", () => {
    setLastInteraction(Date.now());
  }, {
    enabled: chatAssistantOpen
  });
  useWindowEvent("focus", () => {
    setLastInteraction(Date.now());
  }, {
    enabled: chatAssistantOpen
  });
  useIntervalFn(!chatAssistantOpen, () => {
    setLastInteraction(Date.now());
  }, 1e3 * 30);
  useEffect(() => {
    if (!lastInteraction) {
      return;
    }
    if (Date.now() - lastInteraction > MAX_CHAT_INACTIVITY) {
      chatResetRef.current();
    }
  }, [
    lastInteraction
  ]);
  useFabricEvent("fabric-assistant-action", (e) => {
    if (e.detail.type === "summarize") {
      sendMessage(e.detail.text, {
        id: v4(),
        type: ChatbotConversationUserActionType.SUMMARIZE,
        subType: ChatbotConversationUserActionSubType.TEXT
      });
    }
    if (e.detail.type === "add-to-context") {
      if (e.detail.resetContext) {
        setPersistentContext({
          resourceIds: [],
          textSnippets: [
            e.detail.text
          ]
        });
      } else {
        setPersistentContext((prev) => ({
          ...prev,
          textSnippets: [
            ...prev.textSnippets,
            e.detail.text
          ]
        }));
      }
    }
  });
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  useFabricEvent("fabric-ai-diff-status", (e) => {
    if (e.detail.source === "assistant") return;
    const { messageId, pending, failed } = e.detail;
    if (failed) {
      setMessages((prevMessages) => prevMessages.map((msg) => msg.id === messageId ? {
        ...msg,
        hasFailedDiffs: true
      } : msg));
    }
    const lastMessage = messagesRef.current[messagesRef.current.length - 1];
    if (!lastMessage || lastMessage.role !== ChatbotMessageRole.assistant) {
      setDiffsActiveMessageId(null);
      return;
    }
    if (lastMessage.id === messageId && pending) {
      setDiffsActiveMessageId(lastMessage.id);
    } else {
      setDiffsActiveMessageId(null);
    }
  });
  useEffect(() => {
    const lastMessage = messagesRef.current[messagesRef.current.length - 1];
    if (!lastMessage || lastMessage.role !== ChatbotMessageRole.assistant || !expandedResourceId) {
      return;
    }
    dispatchFabricEvent("fabric-ai-diff-status", {
      source: "assistant",
      messageId: lastMessage.id,
      resourceId: expandedResourceId
    });
  }, [
    expandedResourceId
  ]);
  useFabricEvent("fabric-ai-diff-apply", (e) => {
    const { messageId, source } = e.detail;
    if (!messageId || source === "assistant") return;
    setDiffsActiveMessageId(null);
  });
  return /* @__PURE__ */ jsx(ChatsContext.Provider, {
    value,
    children
  });
}
const useChatbot = () => {
  return useContext(ChatsContext);
};

export { MessagesProvider, assistantStoreName, useChatbot };
//# sourceMappingURL=useChatbot.js.map
